import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Row, Col, Select, DatePicker, Modal, Space, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { PageHeaderSection } from '../../components/common/PageHeadSection';
import { useAppDispatch, useAppSelector } from '../../store/Hooks';
import dayjs from 'dayjs';
import { AuthService } from '../../services/AuthService';
import { mdlUserInfo } from '../../models/domain/UserInfo';
import { mdlUpdateUserAccountRequest } from '../../models/service-models/auth/UserAccountRequest';
import { languages } from "../../consts/Languages";
import { setLanguage } from '../../store/SiteSlice';

const UserAccount = () => {
    const { t } = useTranslation();
    const user = useAppSelector((state) => state.site.user);
    const [form] = Form.useForm();
    const [passwordForm] = Form.useForm();
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [userData, setUserData] = useState<mdlUserInfo | null>(null);
    const dispatch = useAppDispatch();
    const language = useAppSelector((state) => state.site.language);
    useEffect(() => {
        loadUserData();
    }, []);

    const loadUserData = async () => {
        if (user?.userName) {
            const response = await AuthService.GetUserAccount({
                username: user.userName
            });
            if (response.header.success && response.body?.userInfo) {
                const userInfo = response.body.userInfo;
                setUserData(userInfo);
                form.setFieldsValue({
                    title: userInfo.title,
                    firstName: userInfo.firstName,
                    lastName: userInfo.lastName,
                    email: userInfo.email,
                    phone: userInfo.phone,
                    mobile: userInfo.mobilePhone,
                    userName: user.userName,
                    language: userInfo.language || userInfo.currentLanguage || user?.currentLanguage || 'tr',
                    birthDate: userInfo.birthDate ? dayjs(userInfo.birthDate) : undefined
                });
            } else {
                message.error(response.header.messages?.[0]?.message || t('common.errorLoading'));
            }
        }
    };

    const onFinish = async (values: mdlUserInfo) => {
        const request: mdlUpdateUserAccountRequest = {
            userId: user?.userId || '',
            username: user?.userName || '',
            currentLanguage: user?.currentLanguage || language,
            title: values.title,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            phone: values.phone,
            mobile: values.mobile,
            birthDate: values.birthDate ? dayjs(values.birthDate).format('YYYY-MM-DD') : undefined,
            language: values.language || values.currentLanguage
        };
        const response = await AuthService.UpdateUserAccount(request);
        if (response.header.success && response.body?.isSuccessful) {
            message.success(t('common.success'));
            if (language !== values.language) {
                dispatch(setLanguage(values.language));
            }
            loadUserData();
            setShowEditModal(false);
        } else {
            message.error(response.header.messages?.[0]?.message || response.body?.message || t('common.errorSaving'));
        }
    };

    const onPasswordChange = async (values: any) => {
        const request = {
            username: user?.userName,
            oldPassword: values.currentPassword,
            newPassword: values.newPassword,
            passwordConfirm: values.confirmPassword,
        };

        const response = await AuthService.ResetPassword(request);
        if (response.header.success) {
            message.success(t('common.success'));
            setShowPasswordModal(false);
            passwordForm.resetFields();
        } else {
            message.error(response.header.messages?.[0]?.message || t('common.errorSaving'));
        }
    };

    return (
        <div className="nk-content">
            <div className="container-fluid">
                <div className="nk-content-inner">
                    <div className="nk-content-body">
                        <PageHeaderSection
                            pageTitle={t('userAccount.pageTitle')}
                            pageDescription={t('userAccount.description')}
                        />
                        <div className="nk-block">
                            <Card
                                extra={
                                    <Space>
                                        <Button type="primary" onClick={() => setShowEditModal(true)}>
                                            {t('common.edit')}
                                        </Button>
                                        <Button onClick={() => setShowPasswordModal(true)}>
                                            {t('userAccount.changePassword')}
                                        </Button>
                                    </Space>
                                }
                            >
                                <div className="mb-4">
                                    <h4>{t('userAccount.personalInformation')}</h4>
                                    <hr />
                                    <Row gutter={[16, 16]}>
                                        <Col span={12}>
                                            <div className="d-flex">
                                                <div style={{ width: '150px', color: '#666' }}>{t('userAccount.title')}:</div>
                                                <div>{userData?.title}</div>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div className="d-flex">
                                                <div style={{ width: '150px', color: '#666' }}>{t('userAccount.firstName')}:</div>
                                                <div>{userData?.firstName}</div>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div className="d-flex">
                                                <div style={{ width: '150px', color: '#666' }}>{t('userAccount.lastName')}:</div>
                                                <div>{userData?.lastName}</div>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div className="d-flex">
                                                <div style={{ width: '150px', color: '#666' }}>{t('userAccount.birthDate')}:</div>
                                                <div>{userData?.birthDate ? dayjs(userData.birthDate).format('DD/MM/YYYY') : '-'}</div>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div className="d-flex">
                                                <div style={{ width: '150px', color: '#666' }}>{t('userAccount.language')}:</div>
                                                <div>{t(languages.find(x => x.value === userData?.language)?.label!)}</div>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div className="d-flex">
                                                <div style={{ width: '150px', color: '#666' }}>{t('userAccount.phone')}:</div>
                                                <div>{userData?.phone || '-'}</div>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div className="d-flex">
                                                <div style={{ width: '150px', color: '#666' }}>{t('userAccount.mobilePhone')}:</div>
                                                <div>{userData?.mobilePhone || '-'}</div>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div className="d-flex">
                                                <div style={{ width: '150px', color: '#666' }}>{t('userAccount.email')}:</div>
                                                <div>{userData?.email}</div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>

                                {/* Account Information Section */}
                                <div className="mb-4">
                                    <h4>{t('userAccount.accountInformation')}</h4>
                                    <hr />
                                    <Row gutter={[16, 16]}>
                                        <Col span={12}>
                                            <div className="d-flex">
                                                <div style={{ width: '150px', color: '#666' }}>{t('userAccount.username')}:</div>
                                                <div>{user?.userName}</div>
                                            </div>
                                        </Col>
                                        <Col span={12}>
                                            <div className="d-flex">
                                                <div style={{ width: '150px', color: '#666' }}>{t('userAccount.password')}:</div>
                                                <div>
                                                    ********
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                title={t('userAccount.editInformation')}
                open={showEditModal}
                onCancel={() => setShowEditModal(false)}
                footer={null}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                >
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="title"
                                label={t('userAccount.title')}
                            >
                                <Select>
                                    <Select.Option value="mr">{t('userAccount.mr')}</Select.Option>
                                    <Select.Option value="mrs">{t('userAccount.mrs')}</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="language"
                                label={t('userAccount.language')}
                                rules={[{ required: true, message: t('common.required') }]}
                            >
                                <Select>
                                    {languages.map((language) => (
                                        <Select.Option key={language.value} value={language.value}>
                                            {t(language.label!)}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="firstName"
                                label={t('userAccount.firstName')}
                                rules={[{ required: true, message: t('common.required') }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="lastName"
                                label={t('userAccount.lastName')}
                                rules={[{ required: true, message: t('common.required') }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="userName"
                                label={t('userAccount.username')}
                            >
                                <Input disabled />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="email"
                                label={t('userAccount.email')}
                                rules={[
                                    { required: true, message: t('common.required') },
                                    { type: 'email', message: t('common.invalidEmail') }
                                ]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="phone"
                                label={t('userAccount.phone')}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="mobile"
                                label={t('userAccount.mobile')}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="birthDate"
                        label={t('userAccount.birthDate')}
                    >
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {t('common.save')}
                            </Button>
                            <Button onClick={() => setShowEditModal(false)}>
                                {t('common.cancel')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={t('userAccount.changePassword')}
                open={showPasswordModal}
                onCancel={() => {
                    setShowPasswordModal(false);
                    passwordForm.resetFields();
                }}
                footer={null}
            >
                <Form
                    form={passwordForm}
                    layout="vertical"
                    onFinish={onPasswordChange}
                >
                    <Form.Item
                        name="username"
                        label={t('userAccount.username')}
                        initialValue={user?.userName}
                    >
                        <Input disabled />
                    </Form.Item>

                    <Form.Item
                        name="currentPassword"
                        label={t('userAccount.currentPassword')}
                        rules={[{ required: true, message: t('userAccount.currentPasswordRequired') }]}
                    >
                        <Input.Password />
                    </Form.Item>

                    <Form.Item
                        name="newPassword"
                        label={t('userAccount.newPassword')}
                        rules={[
                            { required: true, message: t('userAccount.newPasswordRequired') },
                            { min: 8, message: t('userAccount.passwordMinLength') },
                            {
                                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
                                message: t('userAccount.passwordComplexity')
                            }
                        ]}
                    >
                        <Input.Password />
                    </Form.Item>

                    <Form.Item
                        name="confirmPassword"
                        label={t('userAccount.confirmPassword')}
                        dependencies={['newPassword']}
                        rules={[
                            { required: true, message: t('userAccount.confirmPasswordRequired') },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(t('userAccount.passwordsMustMatch')));
                                },
                            }),
                        ]}
                    >
                        <Input.Password />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {t('common.save')}
                            </Button>
                            <Button onClick={() => {
                                setShowPasswordModal(false);
                                passwordForm.resetFields();
                            }}>
                                {t('common.cancel')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default UserAccount; 